import React, { useContext, useMemo, useState } from "react";
import ProductPreview from "./ProductPreview";
import DeviceChoices from "./DeviceChoices";
import PromoCode from "../common/PromoCode";
import PriceView from "../common/PriceView";
import TotalPrice from "../common/TotalPrice";
import ReferralCode from "../common/ReferralCode";
import QuantitySelector from "../common/QuantitySelector";
import { buyNowContext, shippingMethods } from "../BuyNow";
import {DEVICE_TYPES, PRODUCT_CATEGORIES, PURCHASE_FLOWS} from "../../../../constants";
import Subtotal from "./Subtotal";
import { PurchaseSubscriptionAcknowledgement } from "../common/PurchaseSubscriptionAcknowledgement";
import PlanChoicesDropdown from "./PlanChoicesDropdown";
import { GuaranteesIcons } from "../common/GuaranteesIcons";
import {AdminAccessQuantity} from "../common/AdminAccessQuantity";
import {McsMarketplaceAccess} from "../common/McsMarketplaceAccess";

/**
 * This product selection component can be used for multiple clinician purchase flows. There is no text specific to ClearStep / Shapa unless it is explicitly coded per the current purchase flow.
 */
const ClinicianProductSelection = () => {
  const {
    PURCHASE_STAGES,
    setPurchaseStage,
    shippingStageState,
    setShippingStageState,
    scaleBpBundleProducts,
    scaleProducts,
    adminAccessProducts,
    planProducts,
    quantitiesEnabled,
    lineItems,
    removeLineItems,
    setLineItemsByCategory,
    appInfo,
    purchaseFlow,
    CLINICIAN_MODES,
    clinicianMode
  } = useContext(buyNowContext);

  const businessName = appInfo.name;
  const supportEmail = appInfo.supportEmail;
  const clientPurchaseURL = appInfo.clientPurchaseURL;

  const deviceLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.SCALE);
  const adminAccessLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.ADMIN_ACCESS);
  const planLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP);
  const selectedPlan = planLineItem ? planLineItem.product : null;

  // The on-site version of the page is mostly the same, but geared for clinicians that see their clients on-site.
  const isCliniciansOnSite = window.location.pathname.includes("on-site");

  const [selectedDevice, setSelectedDevice] = useState(deviceLineItem ? deviceLineItem.product : null);
  const [previewedDevice, setPreviewedDevice] = useState(null);
  const [isPurchaseSubscriptionAcknowledged, setIsPurchaseSubscriptionAcknowledged] = useState(false);
  const [deviceProductsEnabled, setDeviceProductsEnabled] = useState(!!deviceLineItem || isCliniciansOnSite);
  const [deviceQuantity, setDeviceQuantity] = useState(deviceLineItem ? deviceLineItem.quantity : 0);
  const [deviceQtyExceeded, setDeviceQtyExceeded] = useState(false);

  const licensesAreOnSiteOnly = planProducts.some(p => p.subscriptionType.includes("CS_SITE_LICENSE_ON_SITE_ONLY_LEVEL"));

  // The admin access product is required unless the user is already a registered clinician.
  const isActiveCheckoutSection = useMemo(() => (
    deviceProductsEnabled ? ((isPurchaseSubscriptionAcknowledged || planLineItem) && (deviceLineItem || planLineItem)) : adminAccessLineItem
  ), [deviceProductsEnabled, isPurchaseSubscriptionAcknowledged, deviceLineItem, adminAccessLineItem, planLineItem]);

  const isSelectedDeviceBundle = useMemo(() => {
    return selectedDevice ? selectedDevice.deviceType === DEVICE_TYPES.SCALE_BP_BUNDLE : false;
  }, [selectedDevice]);

  const onToggleDeviceProducts = (event) => {
    setDeviceProductsEnabled(event.target.checked);

    // When this option is either checked or unchecked, let's clear the product line items.
    removeLineItems([deviceLineItem, planLineItem]);
    setDeviceQuantity(0);
    setSelectedDevice(null);
    setIsPurchaseSubscriptionAcknowledged(false);
  };

  const proceedToShipping = () => {
    setPurchaseStage(PURCHASE_STAGES.SHIPPING);
  };

  const checkoutSection = (
    <React.Fragment>
      <PromoCode />
      <ReferralCode />
      <PriceView hideDecimalIfWhole={true} includeShippingCost={false} className="text-center" />
      <section>
        <div className={"disabled-overlay pay-btn-section " + (isActiveCheckoutSection ? 'd-none' : 'd-block')}>
        </div>
        <div className="my-5 text-center">
          <p className="d-block pb-0 checkout-sub-txt">What you pay today</p>
          <h1 className="d-block py-2 display-4 fw-bold total-price-txt">${<TotalPrice hideDecimalIfWhole={true} includeShippingCost={false} />}</h1>
          <div className="text-center py-3">
            <a className="teal button-link next-button-txt next-step-button" onClick={proceedToShipping}>
              Proceed to checkout
            </a>
            <div className="mt-3 text-center">
              <p className="text-muted font-italic most-popular">
                Sales tax and shipping will be calculated at checkout
              </p>
            </div>
          </div>
        </div>
      </section>
    </React.Fragment>
  );

  return (
    <div>
      <div style={{ textAlign: "center" }}>
        <h2 id="buy-now-header" className="col-lg-12 text-lg-center d-lg-block">
          {`${businessName} portal access`}
        </h2>
      </div>
      <GuaranteesIcons/>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-lg-5 offset-lg-1">
            <ProductPreview product={adminAccessProducts[0]} className="py-lg-5" />
          </div>

          <section className="col-lg-5 mt-4">
            <section>
              <div className="custom-control custom-checkbox my-3 col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                <h4 className={"text-dark header-txt"}>
                  {`${businessName} portal access`}
                </h4>

                <span className="description-txt text-muted">
                  {`A one-time registration fee grants lifetime access to the ${businessName} admin portal.`}
                </span>

                <AdminAccessQuantity />

                <div className="form-check d-lg-flex align-items-lg-center col-10 offset-1 px-4 col-lg-12 offset-lg-0 py-3">
                  <input
                    id="alreadyRegisteredClinician"
                    type="checkbox" className="form-check-input me-2"
                    defaultChecked={deviceProductsEnabled}
                    onClick={(event) => onToggleDeviceProducts(event)}
                  />
                  <label className="form-check-label plan-main-txt fw-bolder pt-lg-1" htmlFor="alreadyRegisteredClinician">
                    {'I would like to purchase a scale or membership to support my practice'}
                  </label>
                </div>
              </div>

              {/* The MCS Marketplace access product is only offered to clinicians creating a new organization. */}
              {clinicianMode === CLINICIAN_MODES.NEW_ORGANIZATION &&
                <React.Fragment>
                  <hr className="col-10 offset-1 col-lg-12 offset-lg-0" />
                  <McsMarketplaceAccess />
                </React.Fragment>
              }

              {/* When device products are disabled, place the checkout info below the admin access product. */}
              {!deviceProductsEnabled &&
                <div className="mt-5">
                  {checkoutSection}
                </div>
              }
            </section>
          </section>
        </div>

        <div className="row my-3">
          {deviceProductsEnabled && purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_CLINICIAN ? (
            <div className="col-10 offset-1 col-lg-8 offset-lg-2 my-3">
              <p className={"text-center text-dark p-0 m-0"}>
                Purchasing devices and a group membership allow you to utilize the {businessName} for Admins app within your clinic. You’ll have the ability to generate client accounts and seamlessly capture measurements in person without any effort by your clients.
              </p>
            </div>
          ) : null}
          <div className="col-12 col-lg-5 offset-lg-1">
            {deviceProductsEnabled &&
              <ProductPreview product={previewedDevice || selectedDevice || scaleBpBundleProducts[0]} className="py-lg-5" />
            }
          </div>

          <section className="col-lg-5 mt-4">
            {deviceProductsEnabled &&
              <section>
                <div className="custom-control custom-checkbox my-3 col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                  <h4 className={"text-dark header-txt"}>
                    {`${businessName} products`}
                  </h4>

                  <div>
                    <span className="description-txt text-muted">
                      {`${businessName} products can be purchased to provide to your clients or patients in order to capture health data seamlessly from the comfort of their own home.`}
                    </span>
                  </div>

                  <div className="custom-control custom-checkbox my-3 col-lg-12">
                    <ul>
                      {selectedDevice?.maxCapacity &&
                        <li className='d-flex'>
                          <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                          <p className="pb-1 description-txt">
                            {`Max capacity: ${selectedDevice.maxCapacity} LBs`}
                          </p>
                        </li>
                      }
                      {selectedDevice?.numSensors &&
                        <li className='d-flex'>
                          <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                          <p className="pb-1 description-txt">
                            {`Equipped with ${selectedDevice.numSensors} sensors for bioelectrical impedance for body composition`}
                          </p>
                        </li>
                      }
                      {selectedDevice?.sizeDescription &&
                        <li className='d-flex'>
                          <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                          <p className="pb-1 description-txt">
                            {`${selectedDevice.sizeDescription}`}
                          </p>
                        </li>
                      }
                    </ul>
                  </div>

                  {
                    purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_CLINICIAN && isSelectedDeviceBundle
                    ? <span className="description-txt text-muted">* Scale color may vary between black and oak</span>
                    : null
                  }
                </div>

                <DeviceChoices
                  selectedDevice={selectedDevice}
                  devices={scaleBpBundleProducts.concat(scaleProducts)}
                  onHovered={device => setPreviewedDevice(device)}
                  onDeviceSelected={device => {
                    // If a device doesn't have shipping options available, make sure our state reflects 'standard' shipping.
                    if (!device.shippingOptionsAvailable)
                      setShippingStageState({...shippingStageState, shippingMethod: shippingMethods.standard});

                    setSelectedDevice(device);

                    // When a user initially selects a device, the quantity should be 1.
                    const quantity = Math.max(1, deviceQuantity);

                    setDeviceQuantity(quantity);
                    setLineItemsByCategory([{ product: device, quantity }]);
                  }}
                />

                <div className="custom-control custom-checkbox my-3 col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                  {quantitiesEnabled &&
                    <div>
                      <div className={"disabled-overlay " + (selectedDevice ? 'd-none' : 'd-block')}/>
                      <div className="row">
                        <div className="col-6 mb-5 fw-bold">
                          <h4 className="text-dark header-txt">Quantity</h4>
                          <QuantitySelector
                            quantityValue={deviceQuantity}
                            minVal={0}
                            maxVal={10}
                            onChange={val => {
                              setDeviceQuantity(val);
                              setDeviceQtyExceeded(false);

                              if (val)
                                setLineItemsByCategory([{ product: selectedDevice, quantity: val }]);
                              else
                                removeLineItems(deviceLineItem);
                            }}
                            onMaxBound={() => setDeviceQtyExceeded(true)}
                          />

                          {deviceQtyExceeded &&
                            <div className="sub-txt mt-2" style={{fontStyle: "italic", color: "var(--main-color)"}}>
                              For quantity greater than 10, please contact <a style={{textDecoration: "underline"}} href={`mailto:support@${supportEmail}`}>{supportEmail}</a>
                            </div>
                          }
                        </div>

                        <Subtotal lineItem={deviceLineItem} className="col-6 fw-bold" style={{ textAlign: "right" }} defaultPrice={selectedDevice ? selectedDevice.price : scaleProducts[0].price} />
                      </div>
                    </div>
                  }

                  <div className="description-txt text-muted mb-3">
                    <p><span style={{ fontWeight: "bold" }}>A subscription is required for all numberless scales and bundles.</span> If you are not purchasing a monthly plan, your clients or patients can purchase individual subscriptions <a href={clientPurchaseURL} target="_blank" style={{ textDecoration: "underline", color: "var(--main-color)" }}>here</a>.</p>
                    <PurchaseSubscriptionAcknowledgement setIsPurchaseSubscriptionAcknowledged={setIsPurchaseSubscriptionAcknowledged} />
                  </div>
                </div>

                <div className="custom-control custom-checkbox mt-5 col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                  <h4 className={"text-dark header-txt"}>
                    Monthly Plan
                  </h4>

                  <span className="description-txt text-muted">
                    {!licensesAreOnSiteOnly && `A monthly plan allows multiple clients to connect to ${businessName} devices in-office or remotely at a group rate. This is only required for clinicians wanting to capture measurements in-clinic for clients that have not purchased an individual membership. It is not required to access client data.`}
                    {licensesAreOnSiteOnly && `A monthly plan is designed to support multiple clients on the ${businessName} for Admins app that pairs with MyClearStep scales and blood pressure cuffs to be used in-office. Please select the plan that matches the number of clients you will be capturing measurements for on a monthly basis. These plans can be upgraded/downgraded on a month to month basis.`}
                  </span>

                  <PlanChoicesDropdown
                    selectClassName="description-txt"
                    optionClassName="description-txt"
                    defaultValue={selectedPlan ? selectedPlan.id : null}
                    placeholder="- Select your group rate -"
                    plans={planProducts}
                    onPlanSelected={planId => {
                      // The user selected the default option, meaning we should remove the plan item until they make a new selection.
                      if (planId === "placeholder" && planLineItem) {
                        removeLineItems(planLineItem);
                        return;
                      }

                      const plan = planProducts.find(p => p.id === Number(planId));
                      if (plan !== selectedPlan)
                        setLineItemsByCategory([{ product: plan, quantity: 1 }]);
                    }}
                  />

                  <div className="row mb-2" style={{ marginTop: "50px" }}>
                    <div className="col-6 fw-bold">
                    </div>

                    <Subtotal lineItem={planLineItem} className="col-6 fw-bold" style={{ textAlign: "right" }} defaultPrice={0} />
                  </div>

                  <div className="description-txt my-4 text-muted">
                    If you are interested in a monthly plan for more than 100 clients, please contact us at <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
                  </div>
                </div>
              </section>
            }

            <br/>

            {/* When device products are enabled, place the checkout info below the devices. */}
            {deviceProductsEnabled &&
              <div className="custom-control custom-checkbox my-3 col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                {checkoutSection}
              </div>
            }
          </section>
        </div>
      </div>
    </div>
  );
};

export default ClinicianProductSelection;
